import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction, useState } from "react"
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline"
import { MultiSelectDropdown, Table } from "@/components"
import {
  DirectRatesListingType,
  DirectRatesType,
  MexicoMarketType,
  PortOfEntryType,
} from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import Swal from "sweetalert2"
import { useQuery } from "react-query"
import { api } from "@/lib/api"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { data: DirectRatesListingType[] }
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: DirectRatesType) => void
  handleFormUpdate: (form: DirectRatesType) => void
  handleFormDelete: (form: string) => void
  title: string
  setTitle: (title: string) => void
}

export const DirectRates: FC<Props> = (props) => {
  const [selectedMexicoMarket, setSelectedMexicoMarket] = useState<
    {
      id: string
      name: string
    }[]
  >([])
  const [selectedPortOfEntry, setSelectedPortOfEntry] = useState<
    {
      id: string
      name: string
    }[]
  >([])
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<DirectRatesType>()

  const { data: portOfEntry } = useQuery<{ data: PortOfEntryType[] }, Error>(
    ["portOfEntry"],
    api.PortOfEntryApi.Read,
  )

  const { data: mexicoMarket } = useQuery<{ data: MexicoMarketType[] }, Error>(
    ["mexicoMarket"],
    api.MexicoMarketApi.Read,
  )

  const onSubmit = (data: DirectRatesType) => {
    data.crossingFee = parseInt(String(data.crossingFee))
    data.ratePerMile = parseInt(String(data.ratePerMile))
    data.mexicoMarket = selectedMexicoMarket.map((e) => e.id)
    data.portOfEntry = selectedPortOfEntry.map((e) => e.id)
    data.portOfEntrySelection =
      selectedPortOfEntry.length > 0 ? "SELECTED" : "ALL"
    data.marketSelection = selectedMexicoMarket.length > 0 ? "SELECTED" : "ALL"
    if (data.id == undefined) {
      props.handleForm(data)
    } else {
      props.handleFormUpdate(data)
    }
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      // color: "#ffffff",
      // background: "#333333",
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })
      }
    })
  }

  const populateData = (e: DirectRatesListingType) => {
    props.setTitle('Update Direct Rates')
    const selectedMexicoMarkets = e.mexicoMarket.filter(
      (entry) => entry != undefined && e.id,
    )
    const selectedPortOfEnteries = e.portOfEntry.filter(
      (entry) => entry != undefined && e.id,
    )
    setSelectedMexicoMarket(selectedMexicoMarkets)
    setSelectedPortOfEntry(selectedPortOfEnteries)
    setValue("id", e.id)
    setValue("mexicoMarketId", e.mexicoMarketId)
    setValue("domesticState", e.domesticState)
    setValue("crossingFee", e.crossingFee)
    setValue("ratePerMile", e.ratePerMile)
    setValue("type", e.type)
    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      mexicoMarketId: "",
      domesticState: "",
      type: "",
      crossingFee: undefined,
      ratePerMile: undefined,
      id: undefined,
    })
    setSelectedMexicoMarket([])
    setSelectedPortOfEntry([])
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="medium"
        title={props.title}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <MultiSelectDropdown
                title="Mexico Market"
                selectedList={selectedMexicoMarket}
                setSelectedList={setSelectedMexicoMarket}
                selectedIds={selectedMexicoMarket}
                listingData={
                  mexicoMarket == undefined ? { data: [] } : mexicoMarket
                }
              />
              <MultiSelectDropdown
                title="Port Of Entries"
                selectedList={selectedPortOfEntry}
                setSelectedList={setSelectedPortOfEntry}
                selectedIds={selectedPortOfEntry}
                listingData={
                  portOfEntry == undefined ? { data: [] } : portOfEntry
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">
                  Direct Rate Modifier
                </label>
                <input
                  className={
                    (errors.crossingFee
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white mt-1 outline-none dark:text-white text-light-2 border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="number"
                  placeholder="Enter Direct Rate Modifier"
                  {...register("crossingFee", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Domestic State</label>
                <input
                  className={
                    (errors.domesticState
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white  mt-1 outline-none dark:text-white text-light-2 border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter Domestic State"
                  {...register("domesticState", { required: true })}
                />
              </fieldset>
            </div>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1  ">Type</label>
              <select
                className={
                  (errors.type ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-primary-1 bg-white mt-1 outline-none dark:text-primary-10 text-light-2 border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                }
                placeholder="Enter Rate Per Mile"
                {...register("type", { required: true })}
              >
                <option value="" hidden>
                  Select Type
                </option>
                <option value="Southbound">Southbound</option>
                <option value="Northbound">Northbound</option>
              </select>
            </fieldset>
          </div>
          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-secondary-1 text-primary-1"
            onClick={handleSubmit(onSubmit)}
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.data.map((e, i) => (
            <tr key={i} className="text-left">
              <td className="whitespace-nowrap p-4 w-52">
                {e.type == "Southbound"
                  ? "Southbound"
                  : e.type == "Northbound"
                    ? "Northbound"
                    : null}
              </td>
              <td className="whitespace-nowrap p-4 w-52">
                {e.mexicoMarket.length > 0
                  ? e.mexicoMarket.map((market, i) => (
                    <p key={i}>
                      {market.name}{" "}
                      {e.mexicoMarket.length > 1 &&
                        i < e.mexicoMarket.length - 1 &&
                        ","}
                    </p>
                  ))
                  : "All"}
              </td>
              <td className="whitespace-nowrap p-4 w-52">
                {e.portOfEntry.length > 0
                  ? e.portOfEntry.map((port, i) => (
                    <p key={i}>
                      {port.name}{" "}
                      {e.portOfEntry.length > 1 &&
                        i < e.portOfEntry.length - 1 &&
                        ","}
                    </p>
                  ))
                  : "All"}
              </td>
              <td className="whitespace-nowrap p-4 w-52">{e.domesticState}</td>
              <td className="whitespace-nowrap p-4 w-52">{e.crossingFee}</td>
              <td className="whitespace-nowrap p-4 w-24">
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => populateData(e)}
                  />
                  <TrashIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => onDelete(e.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
